import type {
  Business,
  BusinessContactsResponse,
  BusinessPeopleResponse,
  ConditionGroup,
  CreateBusinessPayload,
  NextPageParams,
  PaginationMetadata,
  ViewColumn,
} from "@/schema"

import { ApiContext, apiContextBlobFetch, apiContextFetch } from "@/hooks/api/base"
import { appendConditionGroupParams, appendViewSortParams } from "@/hooks/api/condition-group-params"

export interface BusinessesResponse {
  businesses: Business[]
  pagination: PaginationMetadata
}

export function fetchBusinessPeople(
  context: ApiContext,
  businessId: string,
  nextPageParams?: NextPageParams,
  search?: string,
  includeFormer = false
) {
  const params = new URLSearchParams()
  if (nextPageParams) {
    params.set("page", nextPageParams.page.toString())
    params.set("per_page", nextPageParams.per_page.toString())
  }
  if (search) params.set("search", search)
  if (includeFormer) params.set("include_former", "true")
  return apiContextFetch<BusinessPeopleResponse>(context, `businesses/${businessId}/people?${params.toString()}`)
}

export function fetchBusinessContacts(
  context: ApiContext,
  businessId: string,
  nextPageParams?: NextPageParams,
  search?: string
) {
  const params = new URLSearchParams()
  if (nextPageParams) {
    params.set("page", nextPageParams.page.toString())
    params.set("per_page", nextPageParams.per_page.toString())
  }
  if (search) params.set("search", search)
  return apiContextFetch<BusinessContactsResponse>(context, `businesses/${businessId}/contacts?${params.toString()}`)
}

export function fetchBusinesses(
  context: ApiContext,
  viewFilterGroup?: ConditionGroup,
  nextPageParams?: NextPageParams,
  searchTerm?: string,
  viewSort?: { field?: string; direction?: "asc" | "desc" },
  minimal = false,
  listId?: string
) {
  const params = new URLSearchParams()

  if (nextPageParams) {
    params.append("page", nextPageParams.page.toString())
    params.append("per_page", nextPageParams.per_page.toString())
  }

  if (searchTerm) {
    params.append("search", searchTerm)
  }

  if (viewFilterGroup) {
    appendConditionGroupParams(params, viewFilterGroup)
  }

  appendViewSortParams(params, viewSort)

  if (minimal) {
    params.append("minimal", "true")
  }

  if (listId) {
    params.append("list_id", listId)
  }

  return apiContextFetch<BusinessesResponse>(context, `businesses?${params.toString()}`, {
    method: "GET",
  })
}

export function fetchBusiness(context: ApiContext, id: string) {
  return apiContextFetch<Business>(context, `businesses/${id}`)
}

export interface MergeBusinessResponse {
  id: string
}

export function mergeBusiness(context: ApiContext, sourceBusinessId: string, targetBusinessId: string) {
  return apiContextFetch<MergeBusinessResponse>(context, `businesses/${sourceBusinessId}/merge`, {
    method: "POST",
    body: JSON.stringify({ target_business_id: targetBusinessId }),
  })
}

export function createBusiness(context: ApiContext, params: CreateBusinessPayload, overrideBookId?: string) {
  return apiContextFetch<Business>(context, "businesses", {
    method: "POST",
    body: JSON.stringify({ business: params, override_book_id: overrideBookId }),
  })
}

export interface UpdateBusinessPayload {
  custom_fields_attributes?: Record<string, unknown>
  [key: string]: unknown
}

export async function updateBusiness(context: ApiContext, id: string, params: UpdateBusinessPayload) {
  // Check if params contains a File object (profile_picture)
  const hasFile = Object.values(params).some((value) => value instanceof File)

  if (hasFile) {
    // Use FormData for file uploads
    const formData = new FormData()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(`business[${key}]`, value as string | Blob)
      }
    })

    return await apiContextFetch<Business>(context, `businesses/${id}`, {
      method: "PUT",
      body: formData,
      headers: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        "Content-Type": undefined as any,
      },
    })
  }

  // Default JSON behavior
  return apiContextFetch<Business>(context, `businesses/${id}`, {
    method: "PUT",
    body: JSON.stringify({ business: params }),
  })
}

export interface AllBusinessIdsResponse {
  business_ids: string[]
  total_count: number
}

export function fetchAllBusinessIds(context: ApiContext, viewFilterGroup?: ConditionGroup, searchTerm?: string) {
  const params = new URLSearchParams()

  if (searchTerm) {
    params.append("search", searchTerm)
  }

  if (viewFilterGroup) {
    appendConditionGroupParams(params, viewFilterGroup)
  }

  return apiContextFetch<AllBusinessIdsResponse>(context, `businesses/all_ids?${params.toString()}`, {
    method: "GET",
  })
}

export interface BusinessesCsvExportParams {
  viewColumns: ViewColumn[]
  conditionGroup: ConditionGroup
  viewSort?: { field?: string; direction?: "asc" | "desc" }
  // In all-books mode the table shows a forced-visible Book column that is not part of the
  // persisted DataView, so inject it into the export to mirror what the user sees.
  includeBookColumn?: boolean
}

// Mirror the table: the forced-visible Book column sits right after Name, so insert it there
// (not appended) and renumber sort_order so the backend renders columns in the same order.
function withBookColumn(viewColumns: ViewColumn[], includeBookColumn?: boolean): ViewColumn[] {
  if (!includeBookColumn) return viewColumns

  const withoutBook = viewColumns
    .filter((vc) => vc.field !== "book.name" && vc.field !== "book_name")
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
  const bookColumn: ViewColumn = { field: "book.name", name: "Book", is_visible: true, sort_order: 0 }
  const ordered = withoutBook.length > 0 ? [withoutBook[0], bookColumn, ...withoutBook.slice(1)] : [bookColumn]
  return ordered.map((vc, index) => ({ ...vc, sort_order: index }))
}

export async function downloadBusinessesCSV(context: ApiContext, params: BusinessesCsvExportParams): Promise<Blob> {
  const searchParams = new URLSearchParams()

  withBookColumn(params.viewColumns, params.includeBookColumn).forEach((viewColumn, index) => {
    const prefix = `view_columns_attributes[${index}]`
    searchParams.append(`${prefix}[field]`, viewColumn.field)
    if (viewColumn.name) searchParams.append(`${prefix}[name]`, viewColumn.name)
    searchParams.append(`${prefix}[is_visible]`, String(viewColumn.is_visible))
    searchParams.append(`${prefix}[sort_order]`, String(viewColumn.sort_order))
  })

  appendConditionGroupParams(searchParams, params.conditionGroup)
  appendViewSortParams(searchParams, params.viewSort)

  return apiContextBlobFetch(context, `businesses/csv_export?${searchParams.toString()}`)
}

export interface BulkUpdateBusinessesPayload {
  business_ids: string[]
  tag_id?: string
  remove_tag_id?: string
}

export function bulkUpdateBusinesses(context: ApiContext, params: BulkUpdateBusinessesPayload) {
  return apiContextFetch<void>(context, "businesses/bulk_update", {
    method: "PATCH",
    body: JSON.stringify(params),
  })
}

export function bulkDestroyBusinesses(context: ApiContext, businessIds: string[]) {
  return apiContextFetch<Business>(context, `businesses/bulk_destroy`, {
    method: "DELETE",
    body: JSON.stringify({ business_ids: businessIds }),
  })
}

export interface BulkCreateBusinessesPayload {
  file_name?: string
  override_duplicates?: boolean
  records: Array<{
    name: string
    industry?: string
    website_url?: string
    tax_id?: string
    additional_info?: string
    email?: string
    email_type?: string
    mobile_phone?: string
    home_phone?: string
    work_phone?: string
    fax_phone?: string
    other_phone?: string
    address_line1?: string
    address_line2?: string
    address_city?: string
    address_state?: string
    address_zip?: string
    address_country_code?: string
    address_type?: string
    tags?: string
    note?: string
    custom_fields?: Record<string, unknown>
  }>
}

export interface BulkCreateBusinessesResponse {
  list_id: string
}

export function bulkCreateBusinesses(context: ApiContext, params: BulkCreateBusinessesPayload) {
  return apiContextFetch<BulkCreateBusinessesResponse>(context, "businesses/bulk_create", {
    method: "POST",
    body: JSON.stringify(params),
  })
}

export interface BulkMoveBusinessesToBookParams {
  business_ids: string[]
  destination_book_id: string
}

export function bulkMoveBusinessesToBook(context: ApiContext, params: BulkMoveBusinessesToBookParams) {
  return apiContextFetch<void>(context, "business_book_moves", {
    method: "POST",
    body: JSON.stringify(params),
  })
}
